import { AdminValidationService } from '../../../services/admin-validation.service';
import { AliasPageName } from 'src/app/gamification/cv-marketplace-admin/models/gamification-pages.model';
import { ChallengesService } from '../../../services/challenges.service';
import { ChangeDetectorRef, Component, Input, OnInit, NgZone } from '@angular/core';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { HostListener } from '@angular/core';
import { Metric } from '../../../models/challenge-user-details';
import { MetricSelectorComponent } from 'src/app/reusables/metric-selector/metric-selector.component';
import { MetricSelectorService } from 'src/app/reusables/metric-selector/metric-selector.service';
import { PointStorePermissionService } from 'src/app/gamification/marketplace/admin/services/point-store-permission.service';
import { Subscription } from 'rxjs';
import { ToastrManager } from '@niceltd/sol/toastr';


@Component({
    selector: 'cv-challenges-settings',
    templateUrl: './challenges-settings.component.html',
    styleUrls: ['./challenges-settings.component.scss']
})
export class ChallengesSettingsComponent implements OnInit {

    @Input() isCXOne: boolean; 
    cs = null;
    selectedMetrics = [];
    metricNames = {};
    groupLevels = [];
    challengeMetrics = [];
    selectedGroupLevel: number = 0;
    isLoading = true;
    isSaving = false;
    SameDayFlag = false;
    groupTypesByLevel: any;
    selectorDisabled: boolean;
    metricDetails: Metric[];
    moduleContainer: any;
    showChallenges = false;
    editMode = true;
    private subscriptionAdminValidation: Subscription = new Subscription();
    challengeTypes = {
        battleBoats: false,
        shadowBoxing: false,
        freeForAll: false
    };

    constructor(
        private challengesService: ChallengesService,
        public config: CvConfigurationService,
        public metricSelectorService: MetricSelectorService,
        private toastrManager: ToastrManager,
        private cvLocalizationFactory: CVLocalizationService,
        private cdr: ChangeDetectorRef,
        private zone: NgZone,
        private pointStorePermissionService: PointStorePermissionService,
        private adminValidationService: AdminValidationService
    ) {
        this.cs = this.challengesService.data;
    }

    ngOnInit(): void {
        $(document).on('event_SelectedMetricsChanged', this.onSelectedMetricsChangedHandler);
        this.loadMetricSelector(true);
        this.loadSettings();
        this.cs.showChallenges = false;
        this.subscriptionAdminValidation = this.adminValidationService.state$.subscribe(state => {
            this.editMode = state.challenges.editMode;
        });
        if (this.isCXOne) {
            sendMessageCXOne({ hasNonSavedChanges: true });
        }
    }

    ngOnDestroy(): void {
        this.subscriptionAdminValidation.unsubscribe();
    }

    get isFormValid(): boolean {
        return Object.values(this.challengeTypes).some(value => value === true);
    }

    onGroupChange (gLevel: number){
        this.selectedGroupLevel = gLevel;
        this.changeChallenges();
    }

    changeChallenges() {
        if (this.isCXOne) {
            const currentState = this.adminValidationService.state.value;
            this.adminValidationService.state.next({
                ...currentState,
                challenges: {
                    editMode: false,
                    lastIndex: currentState.challenges.lastIndex
                }
            });
            sendMessageCXOne({ hasNonSavedChanges: true });
        }
    }

    loadSettings(): void {
        this.challengesService.loadChallengesSettings().then((data: any) => {
            this.groupTypesByLevel = data.GroupTypes;
            for(var groupLevel in this.groupTypesByLevel) {
                this.groupLevels.splice(0, 0, groupLevel);
            }

            this.metricDetails = data?.metricDetails.map(metric => ({
                metricID: metric?.MetricID,
                abbrev: metric?.MetricAbr,
                name: metric?.MetricName,
                conversionType: metric?.ConversionType,
                direction: metric?.Direction || 0
            }));

            this.selectedGroupLevel = data.SelectedGroupLevel;
            this.selectedMetrics = data.Metrics;
            this.metricNames = data.MetricNames;
            this.challengeMetrics = data.ChallengeMetrics;
            this.SameDayFlag = data.SameDayFlag;
            this.isLoading = false;

            if (data.ActiveChallengeTypes) {
                this.challengeTypes = {
                  battleBoats: data.ActiveChallengeTypes.BattleBoat,
                  shadowBoxing: data.ActiveChallengeTypes.ShadowBoxing,
                  freeForAll: data.ActiveChallengeTypes.FreeForAll
                };
            }
        });
    }

    getLocalizationString(key: string, defaultValue: string): string {
        return this.cvLocalizationFactory.getCachedText("Challenges", key, defaultValue);
      }
    
    async saveSettings() {
        const hasCreateOrEditPermission: boolean = await this.pointStorePermissionService.validatePermission({
            name: `${this.pointStorePermissionService.getCreatePermissionName()},${this.pointStorePermissionService.getEditPermissionName()}`,
            pageName: this.pointStorePermissionService.pageNameFilter(AliasPageName.ChallengesTab),
            newCXoneModalVersion: true, type: 'multiple'
        }, !this.config.isCxOne);

        if (!hasCreateOrEditPermission) {
            return;
        }

        //Disabling button when challenges settings are being saved
        if (this.isSaving) {
            return;
        }

        if (!this.isFormValid && !this.isCXOne) {
            this.toastrManager.showToaster('error', this.getLocalizationString("ChallengeType_Required", "At least one challenge type must be selected"));
            return;
        }

        const challengeSettings = {
            SelectedGroupLevel: this.selectedGroupLevel,
            SameDayFlag: this.SameDayFlag,
            ActiveChallengeTypes: {
                BattleBoats: this.challengeTypes.battleBoats || false,
                ShadowBoxing: this.challengeTypes.shadowBoxing || false,
                FreeForAll: this.challengeTypes.freeForAll || false
            }
        };

        this.isSaving = true;
        try {
            this.challengesService.saveChallengesSettings(challengeSettings).then(() => {
                this.isSaving = false;
                this.loadMetricSelector(true);
                if (this.isCXOne) {
                    const currentState = this.adminValidationService.state.value;
                    this.adminValidationService.state.next({
                        ...currentState,
                        challenges: { 
                            ...currentState.challenges,
                            editMode: true, 
                        }
                    });
                    sendMessageCXOne({ hasNonSavedChanges: false });
                    this.toastrManager.showToaster('success', this.getLocalizationString("Challanges_Saved_Successfully", "Challenges saved successfully!"));
                }
            });
        } catch (e) {
            if (this.isCXOne) {
                this.toastrManager.showToaster('error', this.getLocalizationString("Internal_Server_Error_Try_Again_Later", "Internal server error. Try again later."));
            }
        }

    }

    loadMetricSelector(copy?: boolean): void{
        this.selectorDisabled = true;

        this.challengesService.loadMetricSelector(copy).then((response: any) => {
        this.moduleContainer = $(response);
        var element = $('#challengesMetricSelector');

        element.empty();
        element.append(this.moduleContainer);
        setTimeout(()=>{
            this.selectorDisabled = false;
            }, 0)
        });
    }
    
    removeMetric (index: number) {
        var loginID = this.moduleContainer?.attr('cmp');
        if(!loginID) {
            this.removeMetric(index);       
        }

        let orderedArray = [...this.selectedMetrics];
        orderedArray.splice(index, 1);

        const updateMetric = this.metricDetails.filter(metric => metric.metricID !== this.selectedMetrics[index]);
        this.challengesService.saveSelectorState(orderedArray.join(","), loginID).then((response: any) => {
            this.loadMetricSelector();
            
            setTimeout(()=>{
                this.selectedMetrics = orderedArray;
                this.metricDetails = updateMetric;
            }, 0);
        });
    }

    private onSelectedMetricsChangedHandler = (e: any) => {
        if(!e.result) {
            this.zone.run(() => {
                this.selectedMetrics = e.selectedMetrics;
                this.metricNames = e.metricNames;
                this.cdr.detectChanges();
            });
        }
    };
    async saveMetricList() {
        try {
            const loginID = this.moduleContainer?.attr('cmp');
            const orderedArray = [...this.selectedMetrics];
            await this.challengesService.saveSelectorState(orderedArray.join(","), loginID);
            this.selectedMetrics = orderedArray;
            this.cdr.detectChanges();
        } catch (error) {
            this.toastrManager.showToaster('error', this.getLocalizationString("Internal_Server_Error_Try_Again_Later", "Internal server error. Try again later."));
        }
    }

    public async toggleMetricSelector() {
        const maxMetricSelector = 10;
        this.metricSelectorService.hideMetricFilter = true;
        this.metricSelectorService.enableCategory = true;
        
        let metricSelector = this.metricSelectorService.openModalForMultpleleMetric(
            MetricSelectorComponent, 
            this.metricDetails, 
            maxMetricSelector
        );

        metricSelector.result.then(result => {
            this.zone.run(() => {
                this.metricSelectorService.hideMetricFilter = false;
                this.metricSelectorService.enableCategory = false;
                
                if (result) {
                    this.selectedMetrics = result.map(metric => metric.MetricID);
                    this.metricNames = result.reduce((acc, metric) => {
                        acc[metric.MetricID] = metric.MetricName;
                        return acc;
                    }, {});
                    
                    this.metricDetails = result.map(metric => ({
                        metricID: metric.MetricID,
                        abbrev: metric.MetricAbr,
                        name: metric.MetricName,
                        conversionType: metric.ConversionType,
                        direction: metric.Direction || 0
                    }));

                    this.saveMetricList();
                }
            });
        });
    }

    @HostListener('window:beforeunload', ['$event'])
    validateUnsavedChanges(event: BeforeUnloadEvent) {
        if (this.isCXOne && !this.editMode) {
            event.preventDefault();
            event.returnValue = '';
            return;
        }
    }

    goBack() {
        if (this.isCXOne && !this.editMode) {
            this.adminValidationService.showUnsavedChangesDialog(() => {
                const currentState = this.adminValidationService.state.value;
                this.adminValidationService.state.next({
                    ...currentState,
                    challenges: {
                        editMode: true,
                        lastIndex: currentState.challenges.lastIndex
                    }
                });
                sendMessageCXOne({ hasNonSavedChanges: false });
                this.cs.currentView = 0;
            });
        } else {
            this.cs.currentView = 0;
        }
    }
}
